import { useEffect, useMemo, useRef, useState } from "react";
import { Heart, Activity, Wind, Thermometer, Droplet, Gauge } from "lucide-react";
import { toast } from "sonner";
import { getBreedBaseline } from "@/data/breedVulnerabilities";

// ---------- Simulation constants ----------
export const SPECIES_BASE = {
  dog: { hr: 95, rr: 16, spo2: 98, temp: 37.8, map: 80, etco2: 40 },
  cat: { hr: 140, rr: 24, spo2: 98, temp: 38.2, map: 80, etco2: 40 },
};

// Physiological jeopardy accumulates from un-corrected derangements. At ARREST_AT
// the patient suffers a cardiac arrest (asystole); if the arrest is not reversed
// within DEATH_AFTER sim-seconds the patient dies.
const ARREST_AT = 100;
const DEATH_AFTER = 30;

export const ISSUE_DEFS = {
  apnoea: {
    label: "Apnoea",
    icon: Wind,
    color: "#FF3333",
    description: "Patient stopped breathing. Bag the patient until RR returns.",
    fixHint: "Click 'Manual ventilation' 4× or until SpO₂ recovers.",
  },
  hypoxemia: {
    label: "Hypoxemia",
    icon: Droplet,
    color: "#00FFFF",
    description: "SpO₂ falling. Increase O₂ flow and reduce iso.",
    fixHint: "Set O₂ flow ≥ 2 L/min and Iso ≤ 2% for 15 seconds.",
  },
  hypothermia: {
    label: "Hypothermia",
    icon: Thermometer,
    color: "#9CC6FF",
    description: "Core temperature dropping. Apply active warming.",
    fixHint: "Click 'Active warming' and maintain Iso ≤ 2%.",
  },
  bradycardia: {
    label: "Bradycardia",
    icon: Heart,
    color: "#FF3333",
    description: "Heart rate dangerously low.",
    fixHint: "Reduce Iso ≤ 1.5% or administer Atropine.",
  },
  tachycardia: {
    label: "Tachycardia",
    icon: Heart,
    color: "#FF8C42",
    description: "Heart rate too high — light plane / nociception.",
    fixHint: "Deepen plane (Iso ≥ 2%) and give Fentanyl analgesia.",
  },
  hypertension: {
    label: "Hypertension",
    icon: Gauge,
    color: "#FF8C42",
    description: "MAP too high — insufficient analgesia or light plane.",
    fixHint: "Increase Iso to 2.5–3% and administer Fentanyl.",
  },
  arrhythmia: {
    label: "Arrhythmia (VPCs)",
    icon: Activity,
    color: "#FF3333",
    description: "Ventricular premature complexes on ECG.",
    fixHint: "Administer Lidocaine bolus IV.",
  },
  hypotension: {
    label: "Hypotension",
    icon: Gauge,
    color: "#FF8C42",
    description: "MAP suddenly low — deep plane and/or hypovolaemia, poor perfusion.",
    fixHint: "Reduce Iso ≤ 1.5% and give an IV fluid bolus.",
  },
  lightplane: {
    label: "Light plane (too light)",
    icon: Activity,
    color: "#FF8C42",
    description: "Inadequate depth — reflexes return, movement, rising HR/RR/BP.",
    fixHint: "Deepen the plane (Iso ≥ 2.5%) and ensure analgesia.",
  },
};

export function getNormalRange(species) {
  if (species === "cat")
    return { hr: [100, 180], rr: [16, 40], spo2: [95, 100], temp: [37.5, 39.2], map: [60, 100] };
  return { hr: [60, 140], rr: [10, 30], spo2: [95, 100], temp: [37.0, 39.0], map: [60, 100] };
}

export function outOfRange(value, range) {
  return value < range[0] || value > range[1];
}

// Derive systolic/diastolic around MAP (MAP = DAP + 1/3·pulse-pressure).
function deriveBP(map) {
  const pp = 40; // pulse pressure
  return { sap: Math.round(map + (2 / 3) * pp), dap: Math.round(map - (1 / 3) * pp) };
}

// Infer anaesthetic depth indicators from vaporiser setting.
function deriveDepth(iso) {
  if (iso < 1.0) return { eye: "central", palp: "brisk", jaw: "↑ tone" };
  if (iso <= 2.2) return { eye: "ventromed", palp: "sluggish", jaw: "moderate" };
  return { eye: "central", palp: "absent", jaw: "slack" };
}

const MARKER_LABELS = {
  surgeryStart: "Surgery start",
  surgeryEnd: "Surgery end",
  extubation: "Extubation",
};

const FRESH_ISSUES = {
  apnoea: false,
  hypoxemia: false,
  hypothermia: false,
  bradycardia: false,
  tachycardia: false,
  hypertension: false,
  arrhythmia: false,
  hypotension: false,
  lightplane: false,
};

const freshGrade = () => ({
  ticks: 0,
  inRange: { hr: 0, rr: 0, spo2: 0, temp: 0, map: 0 },
  complications: [], // { key, onset, resolved }
});

const freshStabilize = () => ({
  hypoxemiaTimer: 0,
  bradycardiaTimer: 0,
  tachycardiaTimer: 0,
  hypertensionTimer: 0,
  apnoeaBreaths: 0,
  warmingActive: false,
  atropineUntil: 0,
  fluidsUntil: 0,
  fentanylUntil: 0,
  lidocaineUntil: 0,
  arrhythmiaJitter: 0,
  hypotensionTimer: 0,
  lightplaneTimer: 0,
  sampleAccum: 0,
  ventSupportUntil: 0, // manual ventilation maintains oxygenation for a short window
  jeopardy: 0,         // 0..ARREST_AT accumulated physiological insult
  deathTimer: 0,       // sim-seconds spent in un-reversed arrest
  roscBreaths: 0,      // rescue breaths delivered during arrest
});

/**
 * Owns the entire anaesthetic monitor simulation: physiology tick, complication
 * stabilisation, rolling trend history, 5-minute AVA logging, surgery markers,
 * recovery scoring and all interventions. Returns state + actions for the view.
 */
export function useAnaesthesiaSim(patient) {
  const species = patient?.species || "dog";
  // Breed-adjusted baseline: small per-breed nudges to hr/rr/temp/map so each
  // preset settles to slightly different vitals (the sim drifts toward this base).
  const base = useMemo(() => {
    const b = { ...SPECIES_BASE[species] };
    const offsets = getBreedBaseline(patient);
    Object.keys(offsets).forEach((k) => {
      b[k] = (b[k] ?? 0) + offsets[k];
    });
    return b;
  }, [species, patient]);
  const normalRange = useMemo(() => getNormalRange(species), [species]);

  // Vitals — current displayed values (smooth)
  const [vitals, setVitals] = useState({
    hr: base.hr,
    rr: base.rr,
    spo2: base.spo2,
    temp: base.temp,
    map: base.map,
    etco2: base.etco2,
  });

  // Rolling trend history (last ~5 min of HR/SpO₂) for the side trend strip
  const [history, setHistory] = useState([]);

  // Controls
  const [iso, setIso] = useState(2.0);
  const [o2flow, setO2flow] = useState(2.0);

  // Sim state
  const [running, setRunning] = useState(true);
  const [speed, setSpeed] = useState(1); // 1x, 5x, 10x
  const [elapsed, setElapsed] = useState(0); // simulated seconds

  // Issues
  const [issues, setIssues] = useState({ ...FRESH_ISSUES });

  // Life state: "ok" | "arrest" (asystole) | "dead". criticality mirrors jeopardy 0..100.
  const [lifeState, setLifeState] = useState("ok");
  const [criticality, setCriticality] = useState(0);
  const lifeRef = useRef("ok");

  // Stabilization tracking
  const stabilizeRef = useRef(freshStabilize());

  // Grading / debrief tracking (time-in-range + complication handling)
  const gradeRef = useRef(freshGrade());
  const elapsedRef = useRef(0);
  const prevIssuesRef = useRef({ ...FRESH_ISSUES });

  // AVA log entries
  const [entries, setEntries] = useState([]);
  const nextLogAtRef = useRef(0); // log at t=0, then every 300s

  // AVA record: event timeline, surgery markers, recovery (Sections 4 & 5)
  const [events, setEvents] = useState([]);
  const [markers, setMarkers] = useState({});
  const [recovery, setRecovery] = useState({ quality: "", pain: "", analgesia: "" });

  // Refs for waveforms (live HR/RR)
  const hrRef = useRef(vitals.hr);
  const rrRef = useRef(vitals.rr);
  const spo2AmpRef = useRef(1);
  const ecgAmpRef = useRef(1);
  const respAmpRef = useRef(1);
  const vitalsRef = useRef(vitals);

  useEffect(() => {
    vitalsRef.current = vitals;
    hrRef.current = vitals.hr;
    rrRef.current = vitals.rr;
    // Amplitude scaling: low SpO2 reduces pleth, apnoea/arrest zeros resp, arrest flatlines ECG
    const down = lifeState !== "ok";
    spo2AmpRef.current = down ? 0.05 : Math.max(0.15, vitals.spo2 / 100);
    ecgAmpRef.current = down ? 0 : vitals.hr < 30 ? 0.3 : 1;
    respAmpRef.current = issues.apnoea || down ? 0 : Math.min(1, vitals.rr / 14);
  }, [vitals, issues.apnoea, lifeState]);

  // Main simulation tick (every 250ms real time)
  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => {
      const dtSim = 0.25 * speed; // seconds of sim time per tick
      const s = stabilizeRef.current;
      if (lifeRef.current === "dead") return; // frozen once the patient has died
      const arrested = lifeRef.current === "arrest";
      setElapsed((e) => e + dtSim);

      // Compute targets
      setVitals((v) => {
        // ----- CARDIAC ARREST: no cardiac output, no perfusion -----
        if (lifeRef.current === "arrest") {
          const la = (cur, tgt, rate) => cur + (tgt - cur) * Math.min(1, rate * dtSim);
          return {
            hr: la(v.hr, 0, 1.2),
            rr: 0,
            spo2: Math.max(15, la(v.spo2, 18, 0.5)),
            temp: v.temp,
            map: la(v.map, 0, 1.1),
            etco2: Math.max(3, la(v.etco2, 5, 0.6)), // CO2 delivery to lungs collapses
          };
        }

        const target = { ...base };

        // ---- Isoflurane: dose-dependent CNS / cardiorespiratory depression ----
        // Surgical plane ≈ 1.3–2.5%. Deep settings progressively depress HR, MAP and
        // ventilation (non-linear near the top of the dial → lethal overdose).
        const isoDeep = Math.max(0, iso - 1.5);
        const isoOver = Math.max(0, iso - 3);
        target.hr -= isoDeep * 10 + isoOver * 9;
        target.map -= isoDeep * 9 + isoOver * 10;
        target.rr -= isoDeep * 1.6 + Math.max(0, iso - 3.5) * 3.5;
        if (iso < 1.0) {
          // Too light: sympathetic surge / awareness
          const light = 1.0 - iso;
          target.hr += light * 60;
          target.map += light * 45;
          target.rr += light * 14;
        }

        // ---- Drug effects ----
        if (Date.now() < s.atropineUntil) target.hr += 35; // vagolytic tachycardia
        if (Date.now() < s.fluidsUntil) target.map += 14; // volume support
        if (Date.now() < s.fentanylUntil) {
          // opioid: analgesia (lowers HR/MAP) AND respiratory depression
          target.hr -= 18;
          target.map -= 8;
          target.rr -= 5;
        }

        // ---- Triggered complications ----
        if (issues.hypothermia) target.temp = s.warmingActive ? 37.0 : 34.0;
        if (issues.bradycardia) target.hr = Math.min(target.hr, species === "cat" ? 70 : 34);
        if (issues.tachycardia) target.hr = species === "cat" ? 235 : 190;
        if (issues.hypertension) target.map = Math.max(target.map, 135);
        if (issues.hypotension) target.map = Math.min(target.map, 42);
        if (issues.lightplane) {
          target.hr += 42;
          target.map += 26;
          target.rr += 8;
        }
        if (issues.arrhythmia) {
          s.arrhythmiaJitter = (s.arrhythmiaJitter || 0) + dtSim;
          if (s.arrhythmiaJitter > 0.6) {
            s.arrhythmiaJitter = 0;
            target.hr += (Math.random() - 0.5) * 60;
          }
        }

        // Passive cooling on long cases without active warming
        if (!issues.hypothermia && !s.warmingActive && elapsed > 60) {
          target.temp -= Math.min(1.5, (elapsed - 60) / 600);
        }

        // ---- Oxygenation: depends on BOTH fresh-gas O₂ AND ventilation ----
        const bagging = Date.now() < s.ventSupportUntil;
        const apnoeic = issues.apnoea || v.rr < 4; // not breathing (apnoea or deep resp depression)
        const ventilating = bagging || !apnoeic;
        let spo2Target;
        if (!ventilating) spo2Target = Math.max(20, v.spo2 - 5 * dtSim); // no gas exchange → desat
        else if (o2flow < 0.5) spo2Target = Math.max(45, v.spo2 - 3 * dtSim); // hypoxic mixture
        else if (o2flow < 1) spo2Target = 90;
        else spo2Target = 99;
        if (issues.hypoxemia) spo2Target = Math.min(spo2Target, 80);

        // EtCO₂ climbs when hypoventilating, normal when ventilated
        const etco2Target = !ventilating ? Math.min(90, v.etco2 + 2.2 * dtSim) : 40;

        // ---- Smooth interpolation toward targets ----
        const lerp = (cur, tgt, rate) => cur + (tgt - cur) * Math.min(1, rate * dtSim);
        const hr = lerp(v.hr, target.hr, 0.6);
        const rr = lerp(v.rr, Math.max(0, target.rr), 0.7);
        const spo2 = lerp(v.spo2, spo2Target, 0.5);
        const temp = lerp(v.temp, target.temp, 0.04);
        const map = lerp(v.map, target.map, 0.6);
        const etco2 = lerp(v.etco2, etco2Target, 0.5);

        return {
          hr: Math.max(0, hr),
          rr: Math.max(0, rr),
          spo2: Math.max(15, Math.min(100, spo2)),
          temp: Math.max(28, Math.min(43, temp)),
          map: Math.max(0, Math.min(170, map)),
          etco2: Math.max(0, Math.min(95, etco2)),
        };
      });

      // ===== Jeopardy → cardiac arrest → death model =====
      if (!arrested) {
        const cur = vitalsRef.current;
        const hrCrit = species === "cat" ? 60 : 35;
        let insult = 0;
        if (cur.spo2 < 60) insult += 9;
        else if (cur.spo2 < 75) insult += 5;
        else if (cur.spo2 < 88) insult += 1.5;
        if (cur.map < 30) insult += 8;
        else if (cur.map < 45) insult += 3.5;
        else if (cur.map < 55) insult += 0.8;
        if (cur.hr < hrCrit * 0.6) insult += 7;
        else if (cur.hr < hrCrit) insult += 2.5;
        if (cur.hr > (species === "cat" ? 260 : 240)) insult += 2;
        if (cur.temp < 32) insult += 2;
        else if (cur.temp < 34) insult += 0.4;
        if (cur.temp > 41.5) insult += 3;
        if (iso >= 4.8) insult += 4;
        else if (iso >= 4) insult += 1.5;

        s.jeopardy =
          insult > 0
            ? Math.min(ARREST_AT, (s.jeopardy || 0) + insult * dtSim)
            : Math.max(0, (s.jeopardy || 0) - 3 * dtSim);
        setCriticality(Math.round(s.jeopardy));

        if (s.jeopardy >= ARREST_AT) {
          lifeRef.current = "arrest";
          setLifeState("arrest");
          s.deathTimer = 0;
          s.roscBreaths = 0;
          setIssues((i) => ({
            ...i,
            bradycardia: false,
            tachycardia: false,
            hypertension: false,
            lightplane: false,
          }));
          setEvents((ev) => [
            ...ev,
            { t: elapsedRef.current, kind: "complication", label: "Cardiac arrest (asystole)" },
          ]);
          toast.error("⚠ CARDIAC ARREST — asystole. Resuscitate NOW.", {
            description: "Vaporiser OFF (Iso ≤1%), 100% O₂ ≥2 L/min, ventilate, give atropine.",
          });
        }
      } else {
        // In arrest: count down to death unless the cause is reversed.
        s.deathTimer = (s.deathTimer || 0) + dtSim;
        const causeAddressed = iso <= 1 && o2flow >= 2;
        const resuscitated =
          causeAddressed && (s.roscBreaths || 0) >= 2 && Date.now() < s.atropineUntil;
        if (resuscitated) {
          lifeRef.current = "ok";
          setLifeState("ok");
          s.jeopardy = 65;
          s.deathTimer = 0;
          setCriticality(65);
          setVitals((v) => ({
            ...v,
            hr: species === "cat" ? 90 : 55,
            rr: species === "cat" ? 18 : 12,
            map: 55,
            spo2: 92,
            etco2: 38,
          }));
          setEvents((ev) => [
            ...ev,
            { t: elapsedRef.current, kind: "intervention", label: "ROSC — circulation restored" },
          ]);
          toast.success("ROSC — circulation restored. Patient remains critical.");
        } else if (s.deathTimer >= DEATH_AFTER) {
          lifeRef.current = "dead";
          setLifeState("dead");
          setRunning(false);
          setVitals({ hr: 0, rr: 0, spo2: 0, temp: vitalsRef.current.temp, map: 0, etco2: 0 });
          setEvents((ev) => [
            ...ev,
            { t: elapsedRef.current, kind: "complication", label: "Death — resuscitation unsuccessful" },
          ]);
          toast.error("Patient has died. Resuscitation was unsuccessful.", { duration: 8000 });
          return;
        }
      }

      // Stabilization checks
      // Hypoxemia: O2 ≥ 2 AND iso ≤ 2 for 15s
      if (issues.hypoxemia) {
        if (o2flow >= 2 && iso <= 2) {
          s.hypoxemiaTimer += dtSim;
          if (s.hypoxemiaTimer >= 15) {
            setIssues((i) => ({ ...i, hypoxemia: false }));
            s.hypoxemiaTimer = 0;
            toast.success("Hypoxemia resolved. SpO₂ recovering.");
          }
        } else {
          s.hypoxemiaTimer = Math.max(0, s.hypoxemiaTimer - dtSim);
        }
      }
      // Bradycardia: iso ≤ 1.5
      if (issues.bradycardia) {
        if (iso <= 1.5) {
          s.bradycardiaTimer += dtSim;
          if (s.bradycardiaTimer >= 10) {
            setIssues((i) => ({ ...i, bradycardia: false }));
            s.bradycardiaTimer = 0;
            toast.success("Bradycardia resolved. HR returning to normal.");
          }
        } else {
          s.bradycardiaTimer = 0;
        }
      }
      // Hypothermia: warming active for 30s
      if (issues.hypothermia && s.warmingActive) {
        s.hypothermiaTimer = (s.hypothermiaTimer || 0) + dtSim;
        if (s.hypothermiaTimer >= 30) {
          setIssues((i) => ({ ...i, hypothermia: false }));
          s.hypothermiaTimer = 0;
          toast.success("Hypothermia resolved. Core temp normalizing.");
        }
      }
      // Tachycardia: iso ≥ 2 AND fentanyl active for 10s
      if (issues.tachycardia) {
        if (iso >= 2 && Date.now() < s.fentanylUntil) {
          s.tachycardiaTimer += dtSim;
          if (s.tachycardiaTimer >= 10) {
            setIssues((i) => ({ ...i, tachycardia: false }));
            s.tachycardiaTimer = 0;
            toast.success("Tachycardia resolved. HR returning to normal.");
          }
        } else {
          s.tachycardiaTimer = Math.max(0, s.tachycardiaTimer - dtSim);
        }
      }
      // Hypertension: iso ≥ 2.5 OR fentanyl active for 10s
      if (issues.hypertension) {
        if (iso >= 2.5 || Date.now() < s.fentanylUntil) {
          s.hypertensionTimer += dtSim;
          if (s.hypertensionTimer >= 10) {
            setIssues((i) => ({ ...i, hypertension: false }));
            s.hypertensionTimer = 0;
            toast.success("Hypertension resolved. MAP normalizing.");
          }
        } else {
          s.hypertensionTimer = Math.max(0, s.hypertensionTimer - dtSim);
        }
      }
      // Arrhythmia: lidocaine resolves it
      if (issues.arrhythmia && Date.now() < s.lidocaineUntil) {
        setIssues((i) => ({ ...i, arrhythmia: false }));
        toast.success("Arrhythmia resolved. ECG regular.");
      }
      // Hypotension: iso ≤ 1.5 AND fluid bolus running for 12s
      if (issues.hypotension) {
        if (iso <= 1.5 && Date.now() < s.fluidsUntil) {
          s.hypotensionTimer += dtSim;
          if (s.hypotensionTimer >= 12) {
            setIssues((i) => ({ ...i, hypotension: false }));
            s.hypotensionTimer = 0;
            toast.success("Hypotension resolved. MAP recovering.");
          }
        } else {
          s.hypotensionTimer = Math.max(0, s.hypotensionTimer - dtSim);
        }
      }
      // Light plane: deepen the plane (iso ≥ 2.5) for 12s
      if (issues.lightplane) {
        if (iso >= 2.5) {
          s.lightplaneTimer += dtSim;
          if (s.lightplaneTimer >= 12) {
            setIssues((i) => ({ ...i, lightplane: false }));
            s.lightplaneTimer = 0;
            toast.success("Anaesthetic depth restored. Reflexes settling.");
          }
        } else {
          s.lightplaneTimer = Math.max(0, s.lightplaneTimer - dtSim);
        }
      }

      // Sample vitals into the rolling trend history (~every 4 sim-seconds, last 5 min)
      s.sampleAccum = (s.sampleAccum || 0) + dtSim;
      if (s.sampleAccum >= 4) {
        s.sampleAccum = 0;
        setHistory((h) => {
          const next = [...h, { hr: vitalsRef.current.hr, spo2: vitalsRef.current.spo2, rr: vitalsRef.current.rr, map: vitalsRef.current.map }];
          return next.length > 75 ? next.slice(-75) : next;
        });
      }

      // Accumulate time-in-range stats for the end-of-session debrief
      const g = gradeRef.current;
      const vr = vitalsRef.current;
      g.ticks += 1;
      if (!outOfRange(vr.hr, normalRange.hr)) g.inRange.hr += 1;
      if (!outOfRange(vr.rr, normalRange.rr)) g.inRange.rr += 1;
      if (vr.spo2 >= 92) g.inRange.spo2 += 1;
      if (!outOfRange(vr.temp, normalRange.temp)) g.inRange.temp += 1;
      if (!outOfRange(vr.map, normalRange.map)) g.inRange.map += 1;
      elapsedRef.current += dtSim;
    }, 250);

    return () => clearInterval(interval);
    // Deps are deliberate: this physiology loop is driven by the listed values; the
    // refs and state setters it also uses are stable. Do NOT add the loop-body locals
    // (dtSim, lerp, target, etc.) — doing so re-subscribes the timer every tick.
  }, [running, speed, iso, o2flow, issues, base, species, elapsed, normalRange]);

  // AVA log auto-entry every 5 sim-minutes (300s), with first entry at t=0
  useEffect(() => {
    if (!running) return;
    if (elapsed >= nextLogAtRef.current) {
      const noteParts = [];
      Object.entries(issues).forEach(([k, v]) => {
        if (v) noteParts.push(ISSUE_DEFS[k].label);
      });
      const entry = {
        t: nextLogAtRef.current,
        hr: Math.round(vitals.hr),
        rr: Math.round(vitals.rr),
        spo2: Math.round(vitals.spo2),
        temp: vitals.temp,
        map: Math.round(vitals.map),
        etco2: Math.round(vitals.etco2),
        ...deriveBP(vitals.map),
        ...deriveDepth(iso),
        iso,
        o2: o2flow,
        notes: noteParts.join(", "),
      };
      setEntries((prev) => [...prev, entry]);
      nextLogAtRef.current += 300;
    }
  }, [elapsed, running, vitals, iso, o2flow, issues]);

  // Track complication onset/resolution transitions for the debrief grading.
  useEffect(() => {
    const prev = prevIssuesRef.current;
    const now = elapsedRef.current;
    Object.keys(issues).forEach((k) => {
      if (issues[k] && !prev[k]) {
        gradeRef.current.complications.push({ key: k, onset: now, resolved: null });
      } else if (!issues[k] && prev[k]) {
        const open = [...gradeRef.current.complications]
          .reverse()
          .find((x) => x.key === k && x.resolved == null);
        if (open) open.resolved = now;
      }
    });
    prevIssuesRef.current = { ...issues };
  }, [issues]);

  // ----- Actions -----
  const logEvent = (kind, label) =>
    setEvents((ev) => [...ev, { t: elapsed, kind, label }]);

  const setMarker = (key) => {
    if (markers[key] != null) return toast.info(`${MARKER_LABELS[key]} already marked.`);
    setMarkers((m) => ({ ...m, [key]: elapsed }));
    logEvent("intervention", `${MARKER_LABELS[key]} marked`);
    toast.success(
      `${MARKER_LABELS[key]} recorded at ${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(
        Math.floor(elapsed % 60)
      ).padStart(2, "0")}`
    );
  };

  const triggerIssue = (key) => {
    if (issues[key]) {
      toast.info(`${ISSUE_DEFS[key].label} already active.`);
      return;
    }
    setIssues((i) => ({ ...i, [key]: true }));
    logEvent("complication", `${ISSUE_DEFS[key].label} onset`);
    toast.error(`⚠ ${ISSUE_DEFS[key].label} triggered.`, {
      description: ISSUE_DEFS[key].fixHint,
    });
  };

  const manualVent = () => {
    const s = stabilizeRef.current;
    s.ventSupportUntil = Date.now() + 8000; // ~8s of supported oxygenation per breath

    // During arrest, manual breaths count toward the resuscitation bundle.
    if (lifeRef.current === "arrest") {
      s.roscBreaths = (s.roscBreaths || 0) + 1;
      setVitals((v) => ({ ...v, spo2: Math.min(99, v.spo2 + 6), etco2: Math.max(5, v.etco2) }));
      logEvent("intervention", "Rescue breath during arrest (IPPV)");
      toast.success(`Rescue breath delivered (${s.roscBreaths}).`);
      return;
    }

    setVitals((v) => ({
      ...v,
      spo2: Math.min(99, v.spo2 + 4),
      etco2: Math.max(35, v.etco2 - 5),
    }));
    logEvent("intervention", "Manual breath delivered (IPPV)");
    if (issues.apnoea) {
      s.apnoeaBreaths += 1;
      if (s.apnoeaBreaths >= 4) {
        setIssues((i) => ({ ...i, apnoea: false }));
        s.apnoeaBreaths = 0;
        toast.success("Spontaneous respiration returned.");
      } else {
        toast.success(`Breath delivered (${s.apnoeaBreaths}/4).`);
      }
    } else {
      toast.success("Assisted breath delivered.");
    }
  };

  const giveAtropine = () => {
    stabilizeRef.current.atropineUntil = Date.now() + 25_000;
    logEvent("drug", "Atropine IV");
    toast.success("Atropine administered IV.");
    if (issues.bradycardia) {
      setTimeout(() => setIssues((i) => ({ ...i, bradycardia: false })), 1500);
    }
  };

  const giveFluids = () => {
    stabilizeRef.current.fluidsUntil = Date.now() + 30_000;
    logEvent("drug", "IV fluid bolus");
    toast.success("IV fluid bolus started.");
  };

  const giveFentanyl = () => {
    stabilizeRef.current.fentanylUntil = Date.now() + 30_000;
    logEvent("drug", "Fentanyl 2 mcg/kg IV");
    toast.success("Fentanyl 2 mcg/kg IV administered.");
  };

  const giveLidocaine = () => {
    stabilizeRef.current.lidocaineUntil = Date.now() + 6_000;
    logEvent("drug", "Lidocaine 2 mg/kg IV");
    toast.success("Lidocaine 2 mg/kg IV administered.");
  };

  const toggleWarming = () => {
    stabilizeRef.current.warmingActive = !stabilizeRef.current.warmingActive;
    logEvent(
      "intervention",
      `Active warming ${stabilizeRef.current.warmingActive ? "ON" : "OFF"}`
    );
    toast.success(
      stabilizeRef.current.warmingActive ? "Active warming ON" : "Active warming OFF"
    );
  };

  const resetSim = () => {
    setVitals({ ...base });
    setIso(2);
    setO2flow(2);
    setIssues({ ...FRESH_ISSUES });
    setEntries([]);
    setHistory([]);
    setEvents([]);
    setMarkers({});
    setRecovery({ quality: "", pain: "", analgesia: "" });
    setElapsed(0);
    nextLogAtRef.current = 0;
    stabilizeRef.current = freshStabilize();
    gradeRef.current = freshGrade();
    prevIssuesRef.current = { ...FRESH_ISSUES };
    elapsedRef.current = 0;
    lifeRef.current = "ok";
    setLifeState("ok");
    setCriticality(0);
    setRunning(true);
    toast.success("Simulation reset.");
  };

  const activeIssueKeys = Object.keys(issues).filter((k) => issues[k]);

  // Compute the end-of-session debrief: time-in-range, complication handling, grade.
  const computeDebrief = () => {
    const g = gradeRef.current;
    const ticks = Math.max(1, g.ticks);
    const perVital = Object.fromEntries(
      Object.entries(g.inRange).map(([k, v]) => [k, Math.round((v / ticks) * 100)])
    );
    const stabilityPct = Math.round(
      Object.values(perVital).reduce((a, b) => a + b, 0) / Object.keys(perVital).length
    );
    const comps = g.complications;
    const triggered = comps.length;
    const resolved = comps.filter((c) => c.resolved != null).length;
    const resolvedPct = triggered === 0 ? 100 : Math.round((resolved / triggered) * 100);
    const resolvedList = comps.filter((c) => c.resolved != null);
    const avgResponse =
      resolvedList.length === 0
        ? null
        : Math.round(
            resolvedList.reduce((a, c) => a + (c.resolved - c.onset), 0) / resolvedList.length
          );
    const died = lifeRef.current === "dead";
    const baseScore =
      triggered === 0
        ? stabilityPct
        : Math.round(stabilityPct * 0.6 + resolvedPct * 0.4);
    const score = died ? 0 : baseScore;
    const grade = died
      ? "F"
      : score >= 90 ? "A" : score >= 80 ? "B" : score >= 70 ? "C" : score >= 60 ? "D" : "F";
    return {
      score,
      grade,
      died,
      stabilityPct,
      perVital,
      triggered,
      resolved,
      resolvedPct,
      avgResponse,
      complications: comps.map((c) => ({ ...c, label: ISSUE_DEFS[c.key].label })),
      elapsed,
      events,
      markers,
      recovery,
    };
  };

  return {
    // state
    vitals,
    setVitals,
    history,
    iso,
    setIso,
    o2flow,
    setO2flow,
    running,
    setRunning,
    speed,
    setSpeed,
    elapsed,
    issues,
    entries,
    events,
    markers,
    recovery,
    setRecovery,
    species,
    normalRange,
    activeIssueKeys,
    lifeState,
    criticality,
    dead: lifeState === "dead",
    arrested: lifeState === "arrest",
    // waveform refs
    hrRef,
    rrRef,
    spo2AmpRef,
    ecgAmpRef,
    respAmpRef,
    // actions
    triggerIssue,
    manualVent,
    giveAtropine,
    giveFluids,
    giveFentanyl,
    giveLidocaine,
    toggleWarming,
    setMarker,
    resetSim,
    computeDebrief,
  };
}
